import { useState, Fragment, MouseEventHandler, useRef, useEffect, useMemo, useCallback } from 'react';
import { CircularProgress, Box, IconButton, Popover } from '@mui/material';
import { useTranslation } from 'react-i18next';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import { Stack } from '@mui/system';
import alertify from 'alertifyjs';

import { IAbsoluteGain, useSeismicStore } from '../../stores/useSeismicStore';
import { useLine3DNavigationStore } from '../../stores/useLine3DNavigationStore';
import { IIntersections } from '../../models/interfaces/IIntersection';
import { useMapStore } from '../../stores/useMapStore';
import { useGetLineProducts, useGetSeismicAttributes } from './api/useSeismicAttributesController';
import { ISurveyMetadata, SeismicAttribute } from 'features/seismic/models/interfaces/ISurveyMetadata';
import { useAPI } from 'hooks/useAPI';
import { IColorbar } from 'features/seismic/models/interfaces/IColorbar';
import { SeismicAttributesSelect } from './SeismicAttributesSelect';
import { useTileSize } from 'features/seismic/hooks/useTileSize';
import { LineType } from 'features/seismic-3d/models/enums/LineType';
import { SurveyType } from 'features/seismic/models/enums/SurveyType';
import { SeismicAttributeBehavior } from 'features/seismic/models/enums/SeismicAttributeBehavior';
import { useAllCalculatedAttributes } from './api/useCalculatedAttributesController';
import { UseAttributesStore } from 'features/seismic/stores/useAttributesStore';
import { Colorbar } from 'features/seismic/utils/colorbarUtils';
import { use3DViewerStore } from 'features/seismic-3d/stores/use3DViewerStore';
import { getParamsFromUrl } from 'features/seismic/utils/seismicUrlUtils';
import { useFeatureFlags } from 'features/seismic/hooks/useFeatureFlags';

interface IMetadataResponse {
    Metadata: ISurveyMetadata;
    Resolutions: number[];
    Colorbar: IColorbar
}

export function SeismicAttributes({setOpenedPopup, setSelectedAttributes, alineFeatureFlags} : Props) {
    const { t } = useTranslation();

    const urlApi ='/Seismic/Volume/Metadata';

    const { execute: executeMainMetadata, isLoading: isLoading } = useAPI<IMetadataResponse>(urlApi, (error) => {
        if (error.message.indexOf('Request failed with status code 401') > -1){
            alertify.alert('Authentication error');
        }
        else if (error.message.indexOf('Network Error') > -1){
            alertify.alert('Network error');
        }
        else {
            alertify.alert('An error ocurred');
        }
    });

    const { map } = useMapStore(state => ({
        map: state.map
    }));

    const { navbarBlocked, setNavbarBlocked } = useSeismicStore(state => ({ navbarBlocked: state.navbarLoading, setNavbarBlocked: state.setNavbarLoading }));

    const { surveyMetadata, setSurveyMetadata, setSurveyMetadataOverlay, setColorBar, setVolumeTokenOverlay,setVolumeToken ,setVolumeTokenSwipe, setSurveyMetadataSwipe, setAbsoluteGain,
        volumeToken, scale, setAttributes, attributeVersion, setBaseCalculatedAttributeId, setSwipeCalculatedAttributeId, setOverlayCalculatedAttributeId, absoluteGain,
        volumeTokenSwipe, volumeTokenOverlay
    } = useSeismicStore(state => ({
        surveyMetadata: state.surveyMetadata,
        setColorBar: state.setColorbar,
        scale: state.scale,
        volumeToken: state.volumeToken,
        setVolumeToken: state.setVolumeToken,
        setVolumeTokenOverlay: state.setVolumeTokenOverlay,
        setVolumeTokenSwipe: state.setVolumeTokenSwipe,
        setAbsoluteGain: state.setAbsoluteGain,
        setSurveyMetadata: state.setSurveyMetadata,
        setSurveyMetadataOverlay: state.setSurveyMetadataOverlay,
        setSurveyMetadataSwipe: state.setSurveyMetadataSwipe,
        setAttributes: state.setAttributes,
        attributeVersion: state.attributeVersion,
        setBaseCalculatedAttributeId: state.setBaseCalculatedAttributeId,
        setSwipeCalculatedAttributeId: state.setSwipeCalculatedAttributeId,
        setOverlayCalculatedAttributeId: state.setOverlayCalculatedAttributeId,
        absoluteGain: state.absoluteGain,
        volumeTokenSwipe: state.volumeTokenSwipe,
        volumeTokenOverlay: state.volumeTokenOverlay,
    }));

    const {
        overlayColorbar,
        swipeColorbar,
        overlayGain,
        swipeGain,
        setSwipeGain,
        setOverlayGain,
        setSwipeColorbar,
        setOverlayColorbar,
        swipeOpacity,
        overlayOpacity,
        setSwipeOpacity,
        setOverlayOpacity,
        setSelectedProductName,
        setOverlaySelectedProductName,
        setSwipeSelectedProductName,
        setAvailableProducts,
        selectedLineName
    } = UseAttributesStore(state => state);

    const [ defaultSwipeGain, setDefaultSwipeGain ] = useState<IAbsoluteGain | null>(null);
    const [ defaultOverlayGain, setDefaultOverlayGain ] = useState<IAbsoluteGain | null>(null);

    const {width: tileWidthDefault} = useTileSize();

    const { lineNumber, lineType } = useLine3DNavigationStore(state => ({
        lineNumber: state.lineNumber,
        lineType: state.lineType
    }));

    useEffect(() => {console.log('ATTRIBUTES VOLUME TOKEN =============>', volumeToken)}, [volumeToken]);
    const featureFlags = useFeatureFlags().featureFlags?.seismic2D;
    const lineSelectorEnabled = useMemo(() => surveyMetadata?.Type === SurveyType.Seismic2D && featureFlags?.LineSelector2D, [featureFlags?.LineSelector2D, surveyMetadata?.Type]);

    const { data: attributesFrom2DLine, isLoading: isLoadingAttributesFrom2DLine, error: errorOnAttributesFrom2DLine } = useGetLineProducts(surveyMetadata?.SurveyToken, lineSelectorEnabled ? selectedLineName : null);
    const { data: attributesFrom3DLine, isLoading: isLoadingAttributesFrom3DLine, refetch: refetchAttributes } = useGetSeismicAttributes(lineSelectorEnabled ? null : volumeToken);
    const { data: calculatedAttributes, isLoading: isCalculatedAttributesLoading, error: errorOnAttributesFrom3DLine } = useAllCalculatedAttributes();

    const dataAttributes = useMemo(() => attributesFrom3DLine ?? attributesFrom2DLine, [attributesFrom2DLine, attributesFrom3DLine]);
    const errorOnAttributes = useMemo(() => errorOnAttributesFrom3DLine || errorOnAttributesFrom2DLine, [errorOnAttributesFrom2DLine, errorOnAttributesFrom3DLine]);
    const isLoadingAttributes = useMemo(() => isLoadingAttributesFrom2DLine || isLoadingAttributesFrom3DLine || (lineType === LineType.Line2D && lineSelectorEnabled && !selectedLineName), [isLoadingAttributesFrom2DLine, isLoadingAttributesFrom3DLine]);

    const [opened, setOpened] = useState(false);

    const iconButtonElementRef = useRef<HTMLButtonElement | null>(null);
    const [baseProductName, setBaseProductName] = useState<string>('');

    const [ swipeAdditionalColorbars, setSwipeAdditionalColorbars ] = useState<IColorbar[]>([]);
    const [ overlayAdditionalColorbars, setOverlayAdditionalColorbars ] = useState<IColorbar[]>([]);
    const [ enableOverlayColorbar, setEnableOverlayColorbar ] = useState<boolean>(true);
    const [ enableSwipeColorbar, setEnableSwipeColorbar ] = useState<boolean>(true);
    const [ enableSwipeGain, setEnableSwipeGain ] = useState<boolean>(true);
    const [ enableOverlayGain, setEnableOverlayGain ] = useState<boolean>(true);
    const { current: { volumeIdentifier } } = useRef(getParamsFromUrl());

    const handleAttributeChange = useCallback(async (token: string, initialOpacity: number) => {

        setNavbarBlocked(true);

        const attribute = dataAttributes?.find(x => x.VolumeToken.toString() === token.toString());

        const gain : IAbsoluteGain = {
            max: 0,
            min: 0
        };

        if (!attribute && token !== surveyMetadata?.VolumeToken) {
            return;
        }

        const response = await executeMainMetadata({ params: {
            filePath: '',
            volumeToken: token,
            tileWidth: tileWidthDefault,
            screenHeight: 893,
            volumeIdentifier: isNaN(parseInt(token)) ? 'volumeToken' : 'volumeId',
            includePercentageCalc: true
        }});

        if (!response) {
            return;
        }

        if (!!surveyMetadata && token === surveyMetadata.VolumeToken) {
            setNavbarBlocked(false);
            return {
                opacity: 1,
                additionalColorbars: [],
                enableColorbar: true,
                enableGain: true,
                gain: {
                    max: surveyMetadata.MaxSampleValue,
                    min: surveyMetadata.MinSampleValue
                },
                metadata: surveyMetadata,
                colorbar: response.Colorbar,
            };
        }

        if (!attribute) {
            return;
        }

        const metadata = response.Metadata;

        if (!!attribute.MaxSampleValue && !!attribute.MinSampleValue && attribute.MaxSampleValue !== 0 && attribute.MinSampleValue !== 0) {
            gain.max = attribute.MaxSampleValue;
            gain.min = attribute.MinSampleValue;
        }
        else {
            gain.max = metadata.MaxSampleValue;
            gain.min = metadata.MinSampleValue;
        }

        let opacity = initialOpacity;
        let colorbar: IColorbar;
        let additionalColorbars : IColorbar[] = [];
        let enableColorbar = true;
        let enableGain = true;

        if (attribute.DefaultColorbar === 'Reds') {
            colorbar = Colorbar.REDS;
            enableColorbar = false;
            enableGain = false;
            opacity = 1;
        }
        else if (attribute.DefaultColorbar === 'jet') {
            additionalColorbars.push(Colorbar.JET);
            colorbar = Colorbar.JET;
            opacity = 0.15;
        }
        else {
            const defaultColorbar = Colorbar.defaultColorbarList.find(x => x.Name === attribute.DefaultColorbar);
            if (!!defaultColorbar) {
                colorbar = defaultColorbar;
            }
            else {
                colorbar = Colorbar.BINARY;
            }
        }

        setNavbarBlocked(false);
        return {
            opacity: attribute.DefaultOpacity ?? opacity,
            colorbar,
            additionalColorbars,
            enableColorbar,
            gain,
            metadata,
            enableGain
        };
    }, [dataAttributes, executeMainMetadata, setNavbarBlocked, surveyMetadata, tileWidthDefault]);

    const handleSwipeAttributeChange = useCallback(async function (token: string) {
        if (token !== '-') {
            const attributeData = await handleAttributeChange(token, 1);
            if (!!attributeData) {
                const {
                    opacity,
                    colorbar,
                    additionalColorbars,
                    enableColorbar,
                    gain,
                    metadata,
                    enableGain
                } = attributeData;
                //setVolumeTokenSwipe(token);
                setSurveyMetadataSwipe(metadata);
                setSwipeGain(gain);
                setSwipeColorbar(colorbar);
                setSwipeAdditionalColorbars(additionalColorbars);
                setEnableSwipeColorbar(enableColorbar);
                setSwipeOpacity(opacity);
                setEnableSwipeGain(enableGain);
            }
        } else {
            setVolumeTokenSwipe('-');
        }
    }, [handleAttributeChange, setSurveyMetadataSwipe, setSwipeColorbar, setSwipeGain, setSwipeOpacity, setVolumeTokenSwipe]);

    const handleOverlayAttributeChange = useCallback(async function (token: string) {
        //alert(token);
        if (token !== '-') {
            const attributeData = await handleAttributeChange(token, 0.15);
            if (!!attributeData) {
                const {
                    opacity,
                    colorbar,
                    additionalColorbars,
                    enableColorbar,
                    gain,
                    metadata,
                    enableGain
                } = attributeData;
                //setVolumeTokenOverlay(token);
                setSurveyMetadataOverlay(metadata);
                setOverlayGain(gain);
                setOverlayColorbar(colorbar);
                setOverlayAdditionalColorbars(additionalColorbars);
                setEnableOverlayColorbar(enableColorbar);
                setOverlayOpacity(opacity);
                setEnableOverlayGain(enableGain);
            }
        } else {
            setVolumeTokenOverlay('-');
        }
    }, [handleAttributeChange, setOverlayColorbar, setOverlayGain, setOverlayOpacity, setSurveyMetadataOverlay, setVolumeTokenOverlay]);

    const handleBaseAttributeChange = useCallback(async function (token: string) {
        if (token === volumeToken) {
            return;
        }

        if (token !== '-') {
            const response = await executeMainMetadata({ params: {
                filePath: '',
                volumeToken: token,
                tileWidth: tileWidthDefault,
                screenHeight: 893,
                volumeIdentifier: isNaN(parseInt(token)) ? 'volumeToken' : 'volumeId',
                includePercentageCalc: true
            }});

            if (response) {
                if (token === '-') {
                    setVolumeToken('-');
                } else {
                    const metadata = response.Metadata;
                    //metadata.VolumeToken = token;
                    setAbsoluteGain({ max: metadata.MaxSampleValue, min: metadata.MinSampleValue });
                    setColorBar(response.Colorbar);
                    setSurveyMetadata(metadata);
                    setVolumeToken(metadata.VolumeToken);
                }
            }
            console.log('changed token ' + token);
        }
    }, [executeMainMetadata, setAbsoluteGain, setColorBar, setSurveyMetadata, setVolumeToken, tileWidthDefault, volumeToken]);

    const defaultCalculatedAttribute = useMemo(() => calculatedAttributes?.find(x => x.CalculationMethod === surveyMetadata?.AttributeCalculationMethod), [surveyMetadata, calculatedAttributes]);
    useEffect(() => {
        if (errorOnAttributes && lineType === LineType.Line2D) {
            setNavbarBlocked(false);
        }
    }, [errorOnAttributes, setNavbarBlocked]);

    useEffect(() => {
        if (dataAttributes) {
            setAttributes(dataAttributes);
        }
    }, [dataAttributes]);

    useEffect(() => {
        if (!defaultSwipeGain && !(swipeGain.max === 0 && swipeGain.min === 0)) {
            setDefaultSwipeGain(swipeGain);
        }
    }, [swipeGain]);

    useEffect(() => {
        if (!defaultOverlayGain && !(overlayGain.max === 0 && overlayGain.min === 0)) {
            setDefaultOverlayGain(overlayGain);
        }
    }, [overlayGain]);

    const drawProjection = (intersectionInformation: IIntersections[], token: string) => {
        if (intersectionInformation && surveyMetadata) {
            //DRAW LAYER OR OVERLAY
        }
    };

    const handleClickButton:MouseEventHandler<HTMLButtonElement> = (event) => {
        setOpened((value) => !value);
    };

    const [ baseSelectedVolToken, setBaseSelectedVolToken ] = useState('-');

    useEffect(() => {
        if (attributeVersion > 0) {
            refetchAttributes();
        }
    }, [attributeVersion]);

    useEffect(() => {
        const currentProduct = dataAttributes?.find(product => product.VolumeToken.toString() === volumeToken?.toString());
        if (!!currentProduct) {
            setSelectedProductName(currentProduct.Product);
        }
    }, [dataAttributes, volumeToken]);

    useEffect(() => {
        const currentProduct = dataAttributes?.find(product => product.VolumeToken.toString() === volumeTokenOverlay?.toString());
        if (!!currentProduct) {
            setOverlaySelectedProductName(currentProduct.Product);
        }
        else if (!volumeTokenOverlay || volumeTokenOverlay === '-') {
            setOverlaySelectedProductName('');
            setSurveyMetadataOverlay(null);
        }
    }, [dataAttributes, volumeTokenOverlay]);

    useEffect(() => {
        const currentProduct = dataAttributes?.find(product => product.VolumeToken.toString() === volumeTokenSwipe?.toString());
        if (!!currentProduct) {
            setSwipeSelectedProductName(currentProduct.Product);
        }
        else if (!volumeTokenSwipe || volumeTokenSwipe === '-') {
            setSwipeSelectedProductName('');
            setSurveyMetadataSwipe(null);
        }
    }, [dataAttributes, volumeTokenSwipe]);

    useEffect(() => {
        !!baseSelectedVolToken && handleBaseAttributeChange(baseSelectedVolToken);
    }, [baseSelectedVolToken]);

    useEffect(() => {
        if (!!volumeToken) {
            setBaseSelectedVolToken(volumeToken);
        }
    }, [volumeToken]);

    useEffect(() => {
        !!volumeTokenOverlay && handleOverlayAttributeChange(volumeTokenOverlay);
    }, [volumeTokenOverlay, dataAttributes]);

    useEffect(() => {
        !!volumeTokenSwipe && handleSwipeAttributeChange(volumeTokenSwipe);
    }, [volumeTokenSwipe, dataAttributes]);

    useEffect(() => {
        if (!!dataAttributes) {
            setAvailableProducts(dataAttributes);
        }
    }, [dataAttributes]);

    const adjustDataAttributes = useCallback((data: SeismicAttribute[] | undefined) => {
        if (surveyMetadata?.Type === SurveyType.Seismic3D) {
            if (lineType === LineType.Inline) {
                let filter = data?.filter((x) => {
                    return (x.Survey3DInfo?.InlineStart <= lineNumber) &&
                           (x.Survey3DInfo?.InlineEnd >= lineNumber) &&
                           ((x.Survey3DInfo?.InlineEnd - lineNumber) % x.Survey3DInfo?.InlineIncrement === 0);
                });
                return filter;
            }
            else {
                let filter = data?.filter((x) => {
                    return (x.Survey3DInfo?.XlineStart <= lineNumber) &&
                    (x.Survey3DInfo?.XlineEnd >= lineNumber) &&
                    ((x.Survey3DInfo?.XlineEnd - lineNumber) % x.Survey3DInfo?.XlineIncrement === 0);
                });
                return filter;
            }
        }
        else {
            // TODO: CÓDIGO PARA 2D
            return data;
        }
    }, [lineNumber, lineType, surveyMetadata]);

    const adjustedAttributes = useMemo(() => adjustDataAttributes(dataAttributes?.sort((a, b) => a.Product.localeCompare(b.Product))), [adjustDataAttributes, dataAttributes]);

    const overlayAttributes = useMemo(() => {
        if (!surveyMetadata) {
            return [];
        }

        const currentBaseAttribute = dataAttributes?.find(x => x.VolumeToken === surveyMetadata?.VolumeToken);
        const baseDomain = currentBaseAttribute?.Domain ?? surveyMetadata.Domain;

        let filtered = dataAttributes?.filter((x) => x.Domain === baseDomain
        ).sort(function (a, b) {
            return a.Product.localeCompare(b.Product);
        });

        return filtered;
    }, [dataAttributes, surveyMetadata]);

    const baseAttributes = useMemo(() => {
        let filtered =  dataAttributes?.sort(function (a, b) {
            return a.Product.localeCompare(b.Product);
        });

        if (filtered?.length === 0){
            return dataAttributes;
        }

        return filtered;
    }, [dataAttributes]);

    const swipeAttributes = useMemo(() => {
        if (!surveyMetadata) {
            return [];
        }

        const currentBaseAttribute = dataAttributes?.find(x => x.VolumeToken === surveyMetadata?.VolumeToken);

        const baseDomain = currentBaseAttribute?.Domain ?? surveyMetadata.Domain;

        let attributesSwipe =  dataAttributes?.filter((x) => x.Domain === baseDomain).sort(function (a, b) {
            return a.Product.localeCompare(b.Product);
        });

        return attributesSwipe;
    }, [dataAttributes, surveyMetadata]);

    useEffect(() => {
        console.log('SWIPE ATTRIBUTES ==============>', swipeAttributes);
    }, [swipeAttributes]);

    if (isLoadingAttributes ) {
        return <CircularProgress />;
    }

    return (
        <Box>
            <Fragment>
                <IconButton
                    id='attributes'
                    color='primary'
                    ref={iconButtonElementRef}
                    onClick={handleClickButton}
                    title={t('attributes')}>
                    <AnalyticsIcon />
                </IconButton>
            </Fragment>

            <Popover
                anchorEl={iconButtonElementRef.current}
                open={opened}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                keepMounted
                onClose={() => setOpened(false)}
            >
                <Stack style={{ backgroundColor: '#041f3b'}} gap={2} padding={1} direction='column' justifyContent='flex-start' visibility={opened ? 'visible': 'hidden'}>
                    {
                        isLoading ?  (
                            <Stack direction={'row'}>
                                <Box marginRight={2}>
                                    <CircularProgress style={{color: 'grey'}}/>
                                </Box>
                                <Box paddingTop={1}>
                                    <strong style={{marginBottom: '7px'}}>Loading attribute information...</strong>
                                </Box>
                            </Stack>
                        )
                            : null
                    }
                    <Box style={{flexDirection: 'row'}}>
                        <SeismicAttributesSelect
                            selectedVolToken={baseSelectedVolToken}
                            setSelectedVolToken={setBaseSelectedVolToken}
                            disabled={navbarBlocked}
                            title={'Base Product'}
                            isClearable={false}
                            selectedDefault={(surveyMetadata && surveyMetadata?.VolumeToken) ? defaultCalculatedAttribute?.Token ?? surveyMetadata?.VolumeToken : '-'}
                            attributes={baseAttributes}
                            allAttributes={dataAttributes}
                            baseProductName={baseProductName}
                            setBaseProductName={setBaseProductName}
                            setOpenedPopup={setOpenedPopup}
                            setSelectedAttributes={setSelectedAttributes}
                            alineFeatureFlags={alineFeatureFlags}
                            calculatedAttributes={calculatedAttributes}
                            onSelectCalculatedAttribute={(attribute) => setBaseCalculatedAttributeId(attribute?.CalculationMethod ?? 0)}
                            isDefaultSelectedCalculated={!!defaultCalculatedAttribute}
                        />
                    </Box>
                    <Stack>
                        <SeismicAttributesSelect
                            selectedVolToken={volumeTokenSwipe ?? '-'}
                            setSelectedVolToken={setVolumeTokenSwipe}
                            disabled={navbarBlocked}
                            title={'Swipe'}
                            isClearable={true}
                            selectedDefault='-'
                            //attributes={dataAttributes?.filter((el) => el.VolumeName.toLowerCase().indexOf('vel') > -1)}
                            attributes={swipeAttributes}
                            allAttributes={dataAttributes}
                            baseProductName={baseProductName}
                            setBaseProductName={setBaseProductName}
                            setOpenedPopup={setOpenedPopup}
                            setSelectedAttributes={setSelectedAttributes}
                            alineFeatureFlags={alineFeatureFlags}
                            calculatedAttributes={calculatedAttributes}
                            onSelectCalculatedAttribute={(attribute) => { setSwipeCalculatedAttributeId(attribute?.CalculationMethod?? 0) }}
                            attributeSettingsMenuProps={{
                                colorbar: swipeColorbar,
                                onChangeColorbar: setSwipeColorbar,
                                gain: swipeGain,
                                onChangeGain: setSwipeGain,
                                opacity: swipeOpacity,
                                onChangeOpacity: setSwipeOpacity,
                                defaultGain: defaultSwipeGain,
                                enableColorbar: enableSwipeColorbar,
                                additionalColorbars: swipeAdditionalColorbars,
                                enableGain: enableSwipeGain,
                            }}
                        />
                    </Stack>
                    <Stack>
                        <SeismicAttributesSelect
                            selectedVolToken={volumeTokenOverlay ?? '-'}
                            setSelectedVolToken={setVolumeTokenOverlay}
                            disabled={navbarBlocked}
                            title={'Overlay'}
                            isClearable={true}
                            selectedDefault='-'
                            //attributes={dataAttributes?.filter((el) => el.VolumeName.toLowerCase().indexOf('vel') > -1)}
                            attributes={overlayAttributes}
                            allAttributes={dataAttributes}
                            baseProductName={baseProductName}
                            setBaseProductName={setBaseProductName}
                            setOpenedPopup={setOpenedPopup}
                            setSelectedAttributes={setSelectedAttributes}
                            alineFeatureFlags={alineFeatureFlags}
                            calculatedAttributes={calculatedAttributes}
                            onSelectCalculatedAttribute={(attribute) => { setOverlayCalculatedAttributeId(attribute?.CalculationMethod?? 0) }}
                            attributeSettingsMenuProps={{
                                colorbar:overlayColorbar,
                                onChangeColorbar:setOverlayColorbar,
                                gain:overlayGain,
                                onChangeGain:setOverlayGain,
                                opacity:overlayOpacity,
                                onChangeOpacity:setOverlayOpacity,
                                defaultGain:defaultOverlayGain,
                                enableColorbar: enableOverlayColorbar,
                                additionalColorbars: overlayAdditionalColorbars,
                                enableGain: enableOverlayGain,
                            }}
                        />
                    </Stack>
                </Stack>
            </Popover>
        </Box>
    );
}

interface Props {
    setOpenedPopup: (value: boolean) => void;
    setSelectedAttributes: (value: {
        baseProductName: string,
        all: SeismicAttribute[]
    } | undefined) => void;
    alineFeatureFlags: boolean;
}